/**
 * The attribute name for global form errors, meaning not related to
 * an input field.
 */
export const GLOBAL_ERROR_ATTR = 'root';

type ErrorTree = { [attr: string]: ErrorNode };

const toAttr = (path: string[], level: number): string =>
  path.slice(0, level + 1).join('.');

/**
 * A single error object.
 */
export class StandardError {
  readonly attr: string;
  readonly path: string[];

  readonly detail: string;
  readonly code: string;

  constructor(attr: string, detail: string, code: string) {
    this.attr = attr;
    this.path = attr.split('.');
    this.detail = detail;
    this.code = code;
  }

  toString(): string {
    return `<StandardError: ${this.attr}[${this.code}]: ${this.detail}>`;
  }
}

/**
 * A node in a recursive structure that represents the error tree.
 *
 * Each node has a name (attr) and a list of its errors (if any). It also
 * has children nodes, which are also ErrorNode objects of their own.
 */
export class ErrorNode {
  readonly attr: string;
  readonly path: string[];
  readonly errors: StandardError[] = [];

  readonly children: ErrorTree = {};

  private _subtreeErrors = 0;

  constructor(attr: string) {
    this.attr = attr;
    this.path = attr.split('.');
  }

  numSubtreeErrors(): number {
    return this._subtreeErrors;
  }

  incSubtreeErrors() {
    this._subtreeErrors += 1;
    return this;
  }
}

/**
 * An error response object. Contains one or more errors.
 */
export class FormErrorResponse {
  /**
   * Create a StandardErrors object from a fetch response object.
   *
   * @param response A fetch response object
   * @returns A promise of a StandardErrors object
   */
  static async fromResponse(response: Response): Promise<FormErrorResponse> {
    const jsonErrors = await response.json();
    return FormErrorResponse.fromJson(jsonErrors);
  }

  /**
   * Parse a JSON object into a StandardErrors object.
   *
   * @param json A JSON object
   * @returns A StandardErrors object
   */
  static fromJson(jsonErrors: any): FormErrorResponse {
    const errors: StandardError[] = [];
    for (const json of jsonErrors) {
      // If attr is null, it's a global error
      const attr = json.attr || GLOBAL_ERROR_ATTR;
      errors.push(new StandardError(attr, json.detail, json.code));
    }
    return new FormErrorResponse(errors);
  }

  // ---

  readonly errorList: StandardError[] = [];
  readonly errorTree: ErrorTree = {};
  readonly errorsLookup: { [attrCode: string]: StandardError } = {};

  private _hasGlobalErrors = 0;
  private _totalErrors = 0;

  constructor(errors: StandardError[]) {
    errors.forEach((error) => this._appendError(error));
  }

  // ---

  numTotalErrors(): number {
    return this._totalErrors;
  }

  numGlobalErrors(): number {
    return this._hasGlobalErrors;
  }

  hasError(attr: string): boolean {
    return this.getErrors(attr).length > 0;
  }

  getErrors(attr: string): StandardError[] {
    const exploded = (attr || GLOBAL_ERROR_ATTR).split('.');
    if (exploded.length === 0) {
      return [];
    }

    let node: ErrorNode = this.errorTree[exploded[0]];
    for (let i = 1; i < exploded.length && node; i++) {
      node = node.children[toAttr(exploded, i)];
    }

    return node ? node.errors : [];
  }

  globalErrors(): StandardError[] {
    return this.getErrors(GLOBAL_ERROR_ATTR);
  }

  forEach(callback: (error: StandardError) => void) {
    this.errorList.forEach(callback);
  }

  // ---

  appendGlobalError(detail: string, code: string): void {
    this.appendError(GLOBAL_ERROR_ATTR, detail, code);
  }

  appendError(attr: string, detail: string, code: string) {
    this._appendError(new StandardError(attr, detail, code));
  }

  private _appendError(error: StandardError) {
    // Check for duplicates
    const attrCode = `${error.attr}[${error.code}]`;
    if (this.errorsLookup.hasOwnProperty(attrCode)) {
      throw new Error(`Duplicate error: ${error}`);
    }

    // Append errors
    this.errorsLookup[attrCode] = error;
    this.errorList.push(error);

    // Insert into the tree
    this._appendErrorNode(this.errorTree, error);

    // Keep the list sorted
    this.errorList.sort((a, b) => a.attr.localeCompare(b.attr));

    // Check if it's a global error
    if (error.attr === GLOBAL_ERROR_ATTR) {
      this._hasGlobalErrors += 1;
    }

    // Added one more error
    this._totalErrors += 1;
  }

  private _appendErrorNode(tree: ErrorTree, error: StandardError, level: number = 0) {
    // Find the current level's attribute ID
    const current = toAttr(error.path, level);

    // Create a new node if it doesn't exist
    let node = tree[current];
    if (!node) {
      node = new ErrorNode(current);
      node.children[current] = node;
    }

    if (node.attr === error.attr) {
      // Push the error to the node when we find the full path
      node.errors.push(error);
    } else {
      // Recurse to the next level
      this._appendErrorNode(node.children, error, level + 1);
    }

    // Increment the subtree total error count
    node.incSubtreeErrors();
  }
}
